export type IntegrationStatus = 'active' | 'paused' | 'error' | 'provisioning';

export type IntegrationScope = 'org' | 'space';

/**
 * Who issues the webhook secret. Most providers ('optr-generated') accept an
 * Optr-generated secret that the user pastes into the provider's webhook
 * settings. Slack-style providers ('provider-issued') issue the secret
 * themselves; the user pastes it into Optr.
 */
export type SecretMode = 'optr-generated' | 'provider-issued';

export interface ProviderSecretField {
  label: string;
  helpText: string;
}

export type HintCode =
  | 'content-type-form-encoded'
  | 'signature-failures'
  | 'high-error-rate'
  | 'stale';

export type HintSeverity = 'info' | 'warning' | 'error';

export interface IntegrationHint {
  message: string;
  severity: HintSeverity;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface SupportedEventType {
  eventType: string;
  description: string;
  /**
   * Hint about how the event arrives (e.g. "automation-nested" for Confluence
   * events delivered via Atlassian Automation rules). Not surfaced in the UI
   * today but kept for completeness.
   */
  deliveryPath?: string;
}

export interface Provider {
  name: string;
  displayName: string;
  summary: string;
  ingestionType: string;
  documentationUrl?: string;
  setupGuideUrl?: string;
  supportedEventTypes: SupportedEventType[];
  /**
   * Routable fields exposed by the provider for routing-rule patterns. The
   * deployed backend ships rich objects with `path`, `type`, `description`,
   * and optional `example` / `enum` / `appliesTo`. We tolerate bare-string
   * entries too (a documented future shape) — `normalizeFilterFields` does
   * the coercion at every read site.
   */
  filterFields?: Array<FilterField | string>;
  /** Defaults to 'optr-generated' on the backend when omitted. */
  secretMode?: SecretMode;
  /** Present only when secretMode === 'provider-issued'. */
  secretField?: ProviderSecretField;
}

/**
 * A single field that routing rule patterns may filter on. Paths are dot-paths
 * into the provider's event envelope (e.g. `data.integrationEvent.data.projectKey`).
 */
export interface FilterField {
  path: string;
  type: 'string' | 'number' | 'boolean';
  description: string;
  /**
   * Sample value. Usually a primitive, but a `keyed` field's example is a
   * whole map (`{ team: ['billing'] }`), so consumers must not assume it is
   * renderable as a string.
   */
  example?: unknown;
  enum?: Array<string | number>;
  /** Event types this field applies to (omit = applies to all). */
  appliesTo?: string[];
  /**
   * Present when the value at `path` is a map of key -> values rather than a
   * leaf (e.g. Datadog's `tagMap`). Rules are written as
   * `{ <path>: { <key>: [values] } }`, so conditions on different keys AND
   * together — which is the only way to require two tags at once, since a flat
   * list cannot repeat its own key in a pattern.
   *
   * `type` describes the map's values, not the map.
   */
  keyed?: {
    /** Label for the key input, e.g. 'Tag key'. */
    keyLabel: string;
    /** Suggested keys. Not a closed set — any key may be typed. */
    keyExamples?: string[];
  };
}

/**
 * Coerce the wire shape (`(FilterField | string)[]`) to a flat `FilterField[]`.
 * Bare strings get defaulted to `type: 'string'` with no description — the
 * UI degrades gracefully.
 */
export function normalizeFilterFields(
  fields: Array<FilterField | string> | undefined,
): FilterField[] {
  if (!fields) return [];
  return fields.map((f) =>
    typeof f === 'string'
      ? { path: f, type: 'string' as const, description: '' }
      : f,
  );
}

export interface Integration {
  integrationId: string;
  provider: string;
  displayName: string;
  ingestionType: string;
  status: IntegrationStatus;
  webhookId: string;
  webhookUrl: string;
  webhookSecret?: string;
  eventFilters?: string[];
  eventCount: number;
  errorCount: number;
  createdAt: string;
  updatedAt: string;
  lastEventAt?: string;
  lastErrorAt?: string;
  hints?: Partial<Record<HintCode, IntegrationHint>>;
  /**
   * Provider-specific config blob (e.g. google-push carries gcpProjectId,
   * pubsubTopic, oidc). Returned un-stripped by the integration endpoints;
   * shape varies per provider, so consumers narrow it themselves.
   */
  config?: Record<string, unknown>;
  /** Present on responses; legacy space-scoped responses may omit. */
  scope?: IntegrationScope;
  orgId?: string;
  userId?: string;
  /** Set on space-scoped integrations; absent on org-scoped. */
  spaceId?: string;
  /** Org-scoped only — number of routing rules attached. */
  ruleCount?: number;
  /** Org-scoped only — events forwarded to at least one space. */
  routedEventCount?: number;
  /**
   * Org-scoped only — events not matched by any rule. This is an info metric,
   * NOT a warning state: unrouted events are a normal outcome.
   */
  unroutedEventCount?: number;
}

export interface IntegrationEvent {
  eventId: string;
  integrationId: string;
  provider: string;
  eventType: string;
  deliveryId?: string;
  status: string;
  processingDurationMs?: number;
  errorMessage?: string;
  createdAt: string;
  /**
   * Whether this event matched a routing rule and was forwarded to at least
   * one space. `false` means it was received but dropped by routing — a
   * normal outcome, not an error.
   */
  routed?: boolean;
  /** Space ids the event was actually delivered to (one per matched rule). */
  matchedTargetSpaceIds?: string[];
  /** Routing rule ids that matched this event. */
  matchedRuleIds?: string[];
  /**
   * Present when the event was routed by a dynamic rule. `matchedTargetSpaceIds`
   * already holds the real resolved space; this trace explains how it was
   * derived (never carries the `__dynamic__` sentinel).
   */
  dynamicResolution?: DynamicResolution;
}

/** How a dynamically-routed event's target space was resolved at event time. */
export interface DynamicResolution {
  /** Resolver that produced the target (e.g. 'sender-personal-space'). */
  resolver: string;
  /** Whether the sender→user lookup was served from cache. */
  cacheHit: boolean;
  /** The optr user id the sender resolved to (`usr-…`). */
  resolvedUserId: string;
  /** Provider-native sender id (Slack `U…`). */
  senderId: string;
  /** Provider-native workspace/team id (Slack `T…`). */
  teamId: string;
}

/** Captured HTTP request details for a webhook delivery. */
export interface IntegrationDeliveryRequest {
  method?: string;
  path?: string;
  headers?: Record<string, string>;
  body?: string;
  bodyEncoding?: string;
  contentType?: string;
  providerDeliveryId?: string;
}

/** Response we returned to the provider. */
export interface IntegrationDeliveryResponse {
  statusCode?: number;
  headers?: Record<string, string>;
  body?: string;
  durationMs?: number;
}

/**
 * Full event detail returned by
 * `GET /api/v1/conn/integrations/:integrationId/events/:eventId`.
 * Extends the metadata from the list endpoint with the captured webhook
 * delivery (request + response).
 */
export interface IntegrationEventDetail extends IntegrationEvent {
  responseStatusCode?: number;
  contentType?: string;
  action?: string;
  orgId?: string;
  s3Key?: string;
  ttl?: number;
  delivery?: {
    eventId: string;
    integrationId: string;
    orgId?: string;
    provider: string;
    receivedAt?: string;
    request: IntegrationDeliveryRequest;
    response: IntegrationDeliveryResponse;
  };
}

/** Result of POST .../events/:eventId/redeliver. */
export interface IntegrationRedeliverResult {
  replayOf: string;
  status: 'accepted' | string;
  message: string;
}

export interface CreateIntegrationInput {
  provider: string;
  displayName: string;
  eventFilters?: string[];
  /** Defaults to 'space' on the backend when omitted. */
  scope?: IntegrationScope;
  /** Required when the provider's secretMode === 'provider-issued'. */
  webhookSecret?: string;
}

export interface UpdateIntegrationInput {
  displayName?: string;
  status?: IntegrationStatus;
  eventFilters?: string[];
  rotateSecret?: boolean;
  /** For provider-issued secrets — replaces the stored value. */
  webhookSecret?: string;
  /**
   * Slack org-scoped only. `true` seeds the dynamic DM→personal-space routing
   * rule; `false` removes it. The rule's presence is the state — there is no
   * separate flag echoed back on the integration.
   */
  autoRouteDmToPersonalSpace?: boolean;
}

/**
 * A single leaf value inside a routing pattern. Mirrors the EventBridge
 * content-filter operator set that the backend validator supports in v1.
 */
export type PatternValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>
  | { prefix: string }
  /** `*` matches any run of characters, so `*x*` is a substring match. */
  | { wildcard: string }
  | { exists: boolean }
  | { 'anything-but': Array<string | number> }
  | { numeric: ['>' | '>=' | '<' | '<=' | '=', number] };

/**
 * Nested JSON pattern. Objects mirror the event envelope; leaves are
 * `PatternValue`s at the path that should be matched.
 */
export type RoutingPattern = { [key: string]: RoutingPattern | PatternValue };

export type RoutingRuleStatus = 'active' | 'paused';

/**
 * Sentinel `targetSpaceId` carried by a dynamic rule. It must NEVER be resolved
 * as a real space — branch on `isDynamicRule` / `dynamicTarget` instead.
 */
export const DYNAMIC_TARGET_SENTINEL = '__dynamic__';

/**
 * Describes a rule whose target space is resolved at event time rather than
 * pointing at a fixed space. Currently the only `kind` is
 * `'sender-personal-space'` (Slack DM → the sender's own personal space);
 * treat it as an open enum.
 */
export interface DynamicTarget {
  kind: 'sender-personal-space' | (string & {});
  /** Optional override for where the sender id is read from the event. */
  senderIdPath?: string;
}

export interface RoutingRule {
  ruleId: string;
  /** For a dynamic rule this holds `DYNAMIC_TARGET_SENTINEL`, not a real space. */
  targetSpaceId: string;
  pattern: RoutingPattern;
  priority: number;
  status: RoutingRuleStatus;
  description?: string;
  createdAt: string;
  updatedAt: string;
  /** Present iff the rule resolves its target space at event time. */
  dynamicTarget?: DynamicTarget;
}

export interface CreateRoutingRuleInput {
  /** Required for a static rule; omitted when `dynamicTarget` is set. */
  targetSpaceId?: string;
  description?: string;
  /** Defaults to 100 server-side. */
  priority?: number;
  /** Defaults to 'active' server-side. */
  status?: RoutingRuleStatus;
  pattern: RoutingPattern;
  /** Set to create a dynamic rule; leave `targetSpaceId` unset when present. */
  dynamicTarget?: DynamicTarget;
}

/** A rule is dynamic iff it carries `dynamicTarget` (or the sentinel target). */
export const isDynamicRule = (
  rule: Pick<RoutingRule, 'dynamicTarget' | 'targetSpaceId'>,
): boolean => !!rule.dynamicTarget || rule.targetSpaceId === DYNAMIC_TARGET_SENTINEL;

/**
 * What a space member sees: which integration event types are forwarded into
 * the space, and via which rule.
 */
export interface RoutedEventType {
  integrationId: string;
  provider: string;
  displayName: string;
  eventType: string;
  eventDescription?: string;
  ruleId: string;
  ruleDescription?: string;
  /** True when the rule pins to specific event types. */
  pinned: boolean;
}

/**
 * One org integration whose routing rules forward events into the current
 * space. Returned by `GET /api/v1/spaces/{id}/conn/subscriptions`. The
 * `rules` array groups per-rule details; `eventTypeCount` is the count of
 * distinct routed event types across all rules.
 */
export interface SubscriptionRule {
  ruleId: string;
  description?: string;
  /** True when the rule pins to specific event types. */
  pinned: boolean;
  /** Event types this rule forwards. Empty when the rule matches all. */
  eventTypes: string[];
}

export interface Subscription {
  integrationId: string;
  provider: string;
  displayName: string;
  ruleCount: number;
  eventTypeCount: number;
  /** All distinct event types routed into this space across every rule. */
  eventTypes: string[];
  /** Most recent event routed into this space from this integration. */
  lastEventAt?: string;
  /** Events routed into this space from this integration. */
  eventCount?: number;
  rules: SubscriptionRule[];
}
